<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';

import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  contact: {
    type: Object,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    required: true,
  },
  initialMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

// Form state
const activeTab = ref('new'); // 'new', 'pending', 'history'
const content = ref('');
const scheduledDate = ref('');
const scheduledTime = ref('');
const recurrenceType = ref('once'); // 'once', 'daily', 'weekly', 'monthly'
const selectedDays = ref([]);
const selectedTimes = ref(['09:00']);
const endDate = ref('');
const editingId = ref(null);

// Attachments state
const attachments = ref([]);
const isUploading = ref(false);
const fileInputRef = ref(null);
const MAX_FILE_SIZE_MB = 40; // 40MB limit

// Days of week
const daysOfWeek = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sab' },
];

// Computed
const uiFlags = computed(() => store.getters['scheduledMessages/getUIFlags']);
const upcomingMessages = computed(() =>
  store.getters['scheduledMessages/getScheduledMessagesByContact'](
    props.contact.id
  )
);
const historyMessages = computed(() =>
  store.getters['scheduledMessages/getHistoryByContact'](props.contact.id)
);

const isSubmitting = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const minDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

const canSubmit = computed(() => {
  // Need at least content or attachments
  const hasContent = content.value.trim() || attachments.value.length > 0;
  if (!hasContent) return false;
  if (recurrenceType.value === 'once') {
    return scheduledDate.value && scheduledTime.value;
  }
  if (recurrenceType.value === 'weekly' && selectedDays.value.length === 0) {
    return false;
  }
  return selectedTimes.value.length > 0;
});

// Methods
const resetForm = () => {
  content.value = '';
  scheduledDate.value = '';
  scheduledTime.value = '';
  recurrenceType.value = 'once';
  selectedDays.value = [];
  selectedTimes.value = ['09:00'];
  endDate.value = '';
  editingId.value = null;
  attachments.value = [];
};

// Attachment methods
const openFilePicker = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = async event => {
  const files = event.target.files;
  if (!files || files.length === 0) return;

  for (const file of files) {
    await uploadAttachment(file);
  }

  // Reset file input
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const uploadAttachment = async file => {
  // Check file size
  if (!checkFileSizeLimit(file, MAX_FILE_SIZE_MB)) {
    useAlert(`Arquivo muito grande. Tamanho máximo: ${MAX_FILE_SIZE_MB}MB`);
    return;
  }

  isUploading.value = true;

  try {
    const result = await uploadFile(file);
    attachments.value.push({
      id: Date.now(), // Temporary ID for UI
      name: file.name,
      type: file.type,
      size: file.size,
      blobKey: result.blobKey,
      blobId: result.blobId,
      fileUrl: result.fileUrl,
    });
  } catch (error) {
    console.error('Error uploading file:', error);
    useAlert('Erro ao enviar arquivo. Tente novamente.');
  } finally {
    isUploading.value = false;
  }
};

const removeAttachment = id => {
  attachments.value = attachments.value.filter(a => a.id !== id);
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageFile = type => {
  return type?.startsWith('image/');
};

const closeModal = () => {
  resetForm();
  emit('close');
};

const toggleDay = day => {
  const index = selectedDays.value.indexOf(day);
  if (index === -1) {
    selectedDays.value.push(day);
  } else {
    selectedDays.value.splice(index, 1);
  }
};

const addTime = () => {
  selectedTimes.value.push('09:00');
};

const removeTime = index => {
  if (selectedTimes.value.length > 1) {
    selectedTimes.value.splice(index, 1);
  }
};

const buildRecurrenceRule = () => {
  if (recurrenceType.value === 'once') {
    return { type: 'once' };
  }

  const rule = {
    type: recurrenceType.value,
    times: selectedTimes.value.filter(t => t),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  if (recurrenceType.value === 'weekly') {
    rule.days_of_week = selectedDays.value.sort((a, b) => a - b);
  }

  if (endDate.value) {
    rule.end_date = endDate.value;
  }

  return rule;
};

const calculateScheduledAt = () => {
  if (recurrenceType.value === 'once') {
    return new Date(
      `${scheduledDate.value}T${scheduledTime.value}`
    ).toISOString();
  }

  // For recurring messages, calculate the first occurrence
  const now = new Date();
  const [hours, minutes] = (selectedTimes.value[0] || '09:00').split(':');

  if (recurrenceType.value === 'daily') {
    const scheduled = new Date();
    scheduled.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    if (scheduled <= now) {
      scheduled.setDate(scheduled.getDate() + 1);
    }
    return scheduled.toISOString();
  }

  if (recurrenceType.value === 'weekly' && selectedDays.value.length > 0) {
    const sortedDays = [...selectedDays.value].sort((a, b) => a - b);
    const currentDay = now.getDay();

    for (const day of sortedDays) {
      if (
        day > currentDay ||
        (day === currentDay && now.getHours() < parseInt(hours, 10))
      ) {
        const diff = day - currentDay;
        const scheduled = new Date();
        scheduled.setDate(scheduled.getDate() + diff);
        scheduled.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        return scheduled.toISOString();
      }
    }

    // Next week
    const diff = 7 - currentDay + sortedDays[0];
    const scheduled = new Date();
    scheduled.setDate(scheduled.getDate() + diff);
    scheduled.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return scheduled.toISOString();
  }

  // Default: tomorrow at first time
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  return tomorrow.toISOString();
};

const handleSubmit = async () => {
  if (!canSubmit.value) return;

  // Validate required IDs
  if (!props.conversationId) {
    useAlert('Erro: Conversa não identificada. Recarregue a página.');
    return;
  }

  if (!props.inboxId) {
    useAlert('Erro: Inbox não identificado. Recarregue a página.');
    return;
  }

  try {
    // Build attachments data for API
    const attachmentsData = attachments.value.map(a => ({
      blob_key: a.blobKey,
      blob_id: a.blobId,
      file_url: a.fileUrl,
      name: a.name,
      type: a.type,
      size: a.size,
    }));

    const payload = {
      content: content.value,
      scheduled_at: calculateScheduledAt(),
      conversation_id: props.conversationId,
      contact_id: props.contact.id,
      inbox_id: props.inboxId,
      recurrence_rule: buildRecurrenceRule(),
      attachments_data: attachmentsData,
    };

    console.log('Scheduling message with payload:', payload);

    if (editingId.value) {
      await store.dispatch('scheduledMessages/update', {
        id: editingId.value,
        ...payload,
      });
      useAlert(t('SCHEDULED_MESSAGES.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('scheduledMessages/create', payload);
      useAlert(t('SCHEDULED_MESSAGES.CREATE_SUCCESS'));
    }

    resetForm();
    loadMessages();
  } catch (error) {
    console.error('Error scheduling message:', error);
    useAlert(t('SCHEDULED_MESSAGES.ERROR'));
  }
};

const editMessage = message => {
  editingId.value = message.id;
  content.value = message.content;

  const rule = message.recurrence_rule || {};
  recurrenceType.value = rule.type || 'once';

  if (rule.type === 'once') {
    const date = new Date(message.scheduled_at);
    scheduledDate.value = date.toISOString().split('T')[0];
    scheduledTime.value = date.toTimeString().slice(0, 5);
  } else {
    selectedTimes.value = rule.times || ['09:00'];
    selectedDays.value = rule.days_of_week || [];
    endDate.value = rule.end_date || '';
  }

  activeTab.value = 'new';
};

const deleteMessage = async id => {
  if (!confirm(t('SCHEDULED_MESSAGES.DELETE_CONFIRM'))) return;

  try {
    await store.dispatch('scheduledMessages/delete', id);
    useAlert(t('SCHEDULED_MESSAGES.DELETE_SUCCESS'));
    loadMessages();
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.ERROR'));
  }
};

const formatDate = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusLabel = status => {
  const labels = {
    pending: 'Pendente',
    processing: 'Processando',
    sent: 'Enviado',
    failed: 'Falhou',
    cancelled: 'Cancelado',
  };
  return labels[status] || status;
};

const getStatusClass = status => {
  const classes = {
    pending: 'bg-n-amber-9/20 text-n-amber-11',
    processing: 'bg-n-blue-9/20 text-n-blue-11',
    sent: 'bg-n-teal-9/20 text-n-teal-11',
    failed: 'bg-n-ruby-9/20 text-n-ruby-11',
    cancelled: 'bg-n-slate-9/20 text-n-slate-11',
  };
  return classes[status] || '';
};

const getRecurrenceLabel = rule => {
  if (!rule || rule.type === 'once') return 'Uma vez';
  if (rule.type === 'daily') return 'Diariamente';
  if (rule.type === 'weekly') {
    const dayLabels = (rule.days_of_week || [])
      .map(d => daysOfWeek.find(day => day.value === d)?.label)
      .filter(Boolean)
      .join(', ');
    return `Semanal: ${dayLabels}`;
  }
  if (rule.type === 'monthly') return 'Mensalmente';
  return '';
};

const loadMessages = async () => {
  await Promise.all([
    store.dispatch('scheduledMessages/fetchUpcoming', {
      contact_id: props.contact.id,
    }),
    store.dispatch('scheduledMessages/fetchHistory', {
      contact_id: props.contact.id,
    }),
  ]);
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      loadMessages();
      // Fill with initial message if not editing and message is empty
      if (!editingId.value && !content.value && props.initialMessage) {
        content.value = props.initialMessage;
      }
    }
  }
);

onMounted(() => {
  if (props.show) {
    loadMessages();
  }
});
</script>

<template>
  <Modal :show="show" :on-close="closeModal" size="medium">
    <div class="flex flex-col h-full max-h-[80vh]">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-n-weak">
        <div class="flex items-center gap-3">
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('SCHEDULED_MESSAGES.TITLE') }}
          </h2>
          <span class="text-sm text-n-slate-11">
            {{ contact.name }}
          </span>
        </div>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
          @click="closeModal"
        >
          <span class="i-lucide-x text-xl" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex border-b border-n-weak">
        <button
          class="px-4 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'new'
              ? 'text-n-brand border-b-2 border-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'new'"
        >
          {{ editingId ? 'Editar' : 'Novo' }}
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'pending'
              ? 'text-n-brand border-b-2 border-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'pending'"
        >
          Agendados ({{ upcomingMessages.length }})
        </button>
        <button
          class="px-4 py-2 text-sm font-medium transition-colors"
          :class="
            activeTab === 'history'
              ? 'text-n-brand border-b-2 border-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeTab = 'history'"
        >
          Historico
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4">
        <!-- New/Edit Form -->
        <div v-if="activeTab === 'new'" class="space-y-4">
          <!-- Message Content -->
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              Mensagem
            </label>
            <textarea
              v-model="content"
              rows="4"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              placeholder="Digite sua mensagem..."
            />
          </div>

          <!-- Attachments Section -->
          <div>
            <label class="block mb-2 text-sm font-medium text-n-slate-12">
              Anexos
            </label>

            <!-- Hidden file input -->
            <input
              ref="fileInputRef"
              type="file"
              multiple
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
              class="hidden"
              @change="handleFileSelect"
            />

            <!-- Attachment previews -->
            <div v-if="attachments.length > 0" class="mb-3 space-y-2">
              <div
                v-for="attachment in attachments"
                :key="attachment.id"
                class="flex items-center gap-3 p-2 rounded-lg bg-n-alpha-2"
              >
                <!-- Image preview -->
                <div
                  v-if="isImageFile(attachment.type)"
                  class="w-12 h-12 rounded overflow-hidden flex-shrink-0"
                >
                  <img
                    :src="attachment.fileUrl"
                    :alt="attachment.name"
                    class="w-full h-full object-cover"
                  />
                </div>
                <!-- File icon for non-images -->
                <div
                  v-else
                  class="w-12 h-12 rounded bg-n-alpha-3 flex items-center justify-center flex-shrink-0"
                >
                  <span class="i-lucide-file text-xl text-n-slate-11" />
                </div>

                <!-- File info -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-n-slate-12 truncate">
                    {{ attachment.name }}
                  </p>
                  <p class="text-xs text-n-slate-11">
                    {{ formatFileSize(attachment.size) }}
                  </p>
                </div>

                <!-- Remove button -->
                <button
                  class="p-1.5 rounded-lg text-n-ruby-11 hover:bg-n-ruby-9/10 transition-colors"
                  @click="removeAttachment(attachment.id)"
                >
                  <span class="i-lucide-x text-sm" />
                </button>
              </div>
            </div>

            <!-- Add attachment button -->
            <button
              type="button"
              :disabled="isUploading"
              class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-brand hover:bg-n-alpha-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="openFilePicker"
            >
              <span
                v-if="isUploading"
                class="i-svg-spinners-ring-resize text-sm"
              />
              <span v-else class="i-lucide-paperclip text-sm" />
              {{ isUploading ? 'Enviando...' : 'Adicionar arquivo' }}
            </button>
          </div>

          <!-- Recurrence Type -->
          <div>
            <label class="block mb-2 text-sm font-medium text-n-slate-12">
              Tipo de Agendamento
            </label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="type in [
                  { value: 'once', label: 'Uma vez' },
                  { value: 'daily', label: 'Diario' },
                  { value: 'weekly', label: 'Semanal' },
                  { value: 'monthly', label: 'Mensal' },
                ]"
                :key="type.value"
                class="px-3 py-1.5 text-sm rounded-lg transition-colors"
                :class="
                  recurrenceType === type.value
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                "
                @click="recurrenceType = type.value"
              >
                {{ type.label }}
              </button>
            </div>
          </div>

          <!-- Once: Date and Time -->
          <div v-if="recurrenceType === 'once'" class="grid grid-cols-2 gap-4">
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">
                Data
              </label>
              <input
                v-model="scheduledDate"
                type="date"
                :min="minDate"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">
                Horario
              </label>
              <input
                v-model="scheduledTime"
                type="time"
                class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>
          </div>

          <!-- Weekly: Days Selection -->
          <div v-if="recurrenceType === 'weekly'">
            <label class="block mb-2 text-sm font-medium text-n-slate-12">
              Dias da Semana
            </label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="day in daysOfWeek"
                :key="day.value"
                class="w-10 h-10 text-sm rounded-full transition-colors"
                :class="
                  selectedDays.includes(day.value)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
                "
                @click="toggleDay(day.value)"
              >
                {{ day.label }}
              </button>
            </div>
          </div>

          <!-- Times (for recurring) -->
          <div v-if="recurrenceType !== 'once'">
            <label class="block mb-2 text-sm font-medium text-n-slate-12">
              Horarios
            </label>
            <div class="space-y-2">
              <div
                v-for="(time, index) in selectedTimes"
                :key="index"
                class="flex items-center gap-2"
              >
                <input
                  v-model="selectedTimes[index]"
                  type="time"
                  class="flex-1 px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
                <button
                  v-if="selectedTimes.length > 1"
                  class="p-2 text-n-ruby-11 hover:bg-n-ruby-9/10 rounded-lg"
                  @click="removeTime(index)"
                >
                  <span class="i-lucide-trash-2 text-sm" />
                </button>
              </div>
              <button
                class="flex items-center gap-1 text-sm text-n-brand hover:underline"
                @click="addTime"
              >
                <span class="i-lucide-plus text-sm" />
                Adicionar horario
              </button>
            </div>
          </div>

          <!-- End Date (for recurring) -->
          <div v-if="recurrenceType !== 'once'">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              Data Final (opcional)
            </label>
            <input
              v-model="endDate"
              type="date"
              :min="minDate"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end gap-2 pt-4">
            <NextButton
              v-if="editingId"
              slate
              faded
              sm
              label="Cancelar"
              @click="resetForm"
            />
            <NextButton
              blue
              sm
              :label="editingId ? 'Atualizar' : 'Agendar'"
              :disabled="!canSubmit || isSubmitting"
              :is-loading="isSubmitting"
              @click="handleSubmit"
            />
          </div>
        </div>

        <!-- Pending Messages -->
        <div v-else-if="activeTab === 'pending'" class="space-y-3">
          <div
            v-if="uiFlags.isFetching"
            class="flex items-center justify-center py-8"
          >
            <span class="i-svg-spinners-ring-resize text-2xl text-n-brand" />
          </div>

          <div
            v-else-if="upcomingMessages.length === 0"
            class="py-8 text-center text-n-slate-11"
          >
            Nenhum agendamento pendente
          </div>

          <div
            v-for="message in upcomingMessages"
            v-else
            :key="message.id"
            class="p-3 border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <p class="text-sm text-n-slate-12 line-clamp-2">
                  {{ message.content || '(Somente anexos)' }}
                </p>
                <div
                  class="flex items-center flex-wrap gap-2 mt-2 text-xs text-n-slate-11"
                >
                  <span class="flex items-center gap-1">
                    <span class="i-lucide-calendar text-sm" />
                    {{ formatDate(message.scheduled_at) }}
                  </span>
                  <span
                    v-if="message.attachments_data?.length"
                    class="flex items-center gap-1"
                  >
                    <span class="i-lucide-paperclip text-sm" />
                    {{ message.attachments_data.length }} anexo(s)
                  </span>
                </div>
                <div
                  v-if="message.recurrence_rule?.type !== 'once'"
                  class="mt-1 text-xs text-n-slate-11"
                >
                  <span class="i-lucide-repeat text-sm" />
                  {{ getRecurrenceLabel(message.recurrence_rule) }}
                </div>
              </div>
              <div class="flex items-center gap-1">
                <NextButton
                  slate
                  ghost
                  xs
                  icon="i-lucide-pencil"
                  @click="editMessage(message)"
                />
                <NextButton
                  ruby
                  ghost
                  xs
                  icon="i-lucide-trash-2"
                  @click="deleteMessage(message.id)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- History -->
        <div v-else-if="activeTab === 'history'" class="space-y-3">
          <div
            v-if="uiFlags.isFetchingHistory"
            class="flex items-center justify-center py-8"
          >
            <span class="i-svg-spinners-ring-resize text-2xl text-n-brand" />
          </div>

          <div
            v-else-if="historyMessages.length === 0"
            class="py-8 text-center text-n-slate-11"
          >
            Nenhum historico de envio
          </div>

          <div
            v-for="message in historyMessages"
            v-else
            :key="message.id"
            class="p-3 border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <p class="text-sm text-n-slate-12 line-clamp-2">
                  {{ message.content || '(Somente anexos)' }}
                </p>
                <div class="flex items-center flex-wrap gap-2 mt-2">
                  <span
                    class="px-2 py-0.5 text-xs rounded-full"
                    :class="getStatusClass(message.status)"
                  >
                    {{ getStatusLabel(message.status) }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ formatDate(message.sent_at || message.updated_at) }}
                  </span>
                  <span
                    v-if="message.attachments_data?.length"
                    class="flex items-center gap-1 text-xs text-n-slate-11"
                  >
                    <span class="i-lucide-paperclip text-sm" />
                    {{ message.attachments_data.length }} anexo(s)
                  </span>
                </div>
                <p
                  v-if="message.error_message"
                  class="mt-1 text-xs text-n-ruby-11"
                >
                  {{ message.error_message }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
